import moment from 'moment';
import { http } from '@/plugins/http';

export const BASE = '/billing-accounts';

export const OTHER_EXPENSE_TYPES = ['gas', 'medicine', 'material', 'daily', 'tax', 'opme'];

export function idOf(doc) {
  if (!doc) return null;
  if (typeof doc === 'string') return doc;
  const { id, _id: objectId } = doc;
  const value = id || objectId || null;
  return value ? String(value) : null;
}

export function kindOf(expenseType) {
  return OTHER_EXPENSE_TYPES.includes(expenseType) ? 'other_expense' : 'procedure';
}

export function isEnabled(user) {
  const settings = user && user.branch && user.branch.settings
    ? user.branch.settings.insuranceBilling
    : null;
  return !!(settings && settings.enabled);
}

export const APPOINTMENT_GENERATION = {
  NONE: 'none',
  LEGACY: 'legacy',
  NEW: 'new',
  MIXED: 'mixed',
};

export function isLegacyExpense(expense) {
  return !!(expense.accountId || (expense.billing && expense.billing.status === 'legacy'));
}

export function isNewBillingExpense(expense) {
  const billing = expense.billing || {};
  return !!((idOf(billing.accountId) && billing.status !== 'legacy') || expense.cashRegisterEntryId);
}

export function billingGeneration(expenses) {
  const items = expenses || [];
  const legacy = items.some(isLegacyExpense);
  const current = items.some(isNewBillingExpense);
  if (legacy && current) return APPOINTMENT_GENERATION.MIXED;
  if (legacy) return APPOINTMENT_GENERATION.LEGACY;
  if (current) return APPOINTMENT_GENERATION.NEW;
  return APPOINTMENT_GENERATION.NONE;
}

export function opensLegacyScreen(expenses) {
  return [APPOINTMENT_GENERATION.LEGACY, APPOINTMENT_GENERATION.MIXED]
    .includes(billingGeneration(expenses));
}

export function isParticularPlan(insurance) {
  if (!insurance) return false;
  const plan = insurance.plan || {};
  return plan.type === 'particular'
    || insurance.type === 'particular'
    || /^particular$/i.test(insurance.name || '');
}

export function normalizeItem(item) {
  if (!item) return null;
  const transfer = item.transfer || {};
  return {
    ...item,
    id: idOf(item),
    appointmentId: idOf(item.appointmentId),
    appointmentExpenseId: idOf(item.appointmentExpenseId),
    expense: item.expense ? { ...item.expense, id: idOf(item.expense) } : null,
    professional: item.professional ? { ...item.professional, id: idOf(item.professional) } : null,
    transfer: { ...transfer, professionalId: idOf(transfer.professionalId) },
    unit: item.unit || { insurance: 0, particular: 0 },
    value: item.value || { insurance: 0, particular: 0, total: 0 },
    price: item.price || { priced: false, source: 'none' },
  };
}

export function normalizeAccount(account) {
  if (!account) return null;
  const insurance = account.insurance || {};
  const plan = insurance.plan || {};
  return {
    ...account,
    id: idOf(account),
    batch: account.batch ? { ...account.batch, id: idOf(account.batch) } : null,
    patient: account.patient ? { ...account.patient, id: idOf(account.patient) } : null,
    professional: account.professional
      ? { ...account.professional, id: idOf(account.professional) }
      : null,
    insurance: {
      ...insurance,
      id: idOf(insurance),
      plan: { ...plan, id: idOf(plan) },
      insured: insurance.insured || { record: null, name: null, validity: null },
    },
    referral: account.referral || { provider: null, insurance: null, main: null },
    authorization: account.authorization || null,
    request: account.request || null,
    service: account.service || {},
    totals: account.totals || { insurance: 0, particular: 0, informed: 0 },
    items: (account.items || []).map(normalizeItem),
  };
}

function normalizeList(data) {
  return {
    ...data,
    items: (data.items || []).map(normalizeAccount),
  };
}

export function list(params = {}) {
  return http.get(BASE, { params }).then(({ data }) => normalizeList(data));
}

export function get(id) {
  return http.get(`${BASE}/${id}`).then(({ data }) => normalizeAccount(data));
}

export function byAppointment(appointmentId) {
  return http.get(`${BASE}/by-appointment/${appointmentId}`)
    .then(({ data }) => normalizeList(data));
}

function withSnapshot(data) {
  return {
    ...normalizeAccount(data),
    appointments: data.appointments || [],
  };
}

export function create(payload) {
  return http.post(BASE, payload).then(({ data }) => withSnapshot(data));
}

export function update(id, payload) {
  return http.put(`${BASE}/${id}`, payload).then(({ data }) => withSnapshot(data));
}

export function remove(id) {
  return http.delete(`${BASE}/${id}`).then(({ data }) => data);
}

export function removeItem(id, itemId) {
  return http.delete(`${BASE}/${id}/items/${itemId}`)
    .then(({ data }) => (data && data.deleted ? data : withSnapshot(data)));
}

export function resolvePrices(items, appointmentId = null) {
  if (!items || items.length === 0) return Promise.resolve([]);
  return http.post('/pricing/resolve', { appointmentId, items })
    .then(({ data }) => data.items || []);
}

const DIFF_KEYS = ['insurance', 'particular', 'transfer'];

function num(value) {
  return Number(value) || 0;
}

function round2(value) {
  return Math.round(value * 100) / 100;
}

function transferAmount(transfer) {
  if (typeof transfer === 'number') return num(transfer);
  if (!transfer) return 0;
  return round2(num(transfer.amount) + num(transfer.requestingAmount));
}

function repriceSide(side) {
  const source = side || {};
  const value = source.value || source;
  const unit = source.unit || {};
  return {
    unit: { insurance: num(unit.insurance), particular: num(unit.particular) },
    insurance: num(value.insurance),
    particular: num(value.particular),
    transfer: transferAmount(source.transfer),
  };
}

function repriceDiff(before, after) {
  return DIFF_KEYS.reduce((diff, key) => ({
    ...diff,
    [key]: round2(after[key] - before[key]),
  }), {});
}

function hasDiff(diff) {
  return DIFF_KEYS.some(key => Math.abs(diff[key]) >= 0.005);
}

function sumSides(rows, side) {
  return rows.reduce((totals, row) => {
    DIFF_KEYS.forEach((key) => {
      totals[key] = round2(totals[key] + row[side][key]);
    });
    return totals;
  }, { insurance: 0, particular: 0, transfer: 0 });
}

function changedCount(items) {
  return (items || []).filter(item => item.changed).length;
}

function repriceItem(item) {
  const source = item || {};
  const expense = source.expense || {};
  const professional = source.professional || {};
  const before = repriceSide(source.from);
  const after = repriceSide(source.to);
  const diff = repriceDiff(before, after);
  return {
    id: idOf(source.itemId) || idOf(source.appointmentExpenseId),
    expense: { code: expense.code || '', name: expense.name || '', type: expense.type || '' },
    professionalName: professional.name || '',
    quantity: Number((source.to || {}).quantity) || 1,
    before,
    after,
    diff,
    changed: source.changed === undefined ? hasDiff(diff) : !!source.changed,
  };
}

function repriceTotals(totals, items, side, key) {
  const source = totals || {};
  return source[key] ? repriceSide(source[key]) : sumSides(items, side);
}

function normalizeReprice(data) {
  const source = data || {};
  const items = (source.items || []).map(repriceItem);
  const before = repriceTotals(source.totals, items, 'before', 'from');
  const after = repriceTotals(source.totals, items, 'after', 'to');
  return {
    id: idOf(source),
    number: source.referral || null,
    items,
    changed: changedCount(items),
    before,
    after,
    diff: repriceDiff(before, after),
  };
}

function blankRow(source) {
  return {
    id: idOf(source),
    number: null,
    code: null,
    patientName: '',
    startDate: null,
    type: null,
    items: 0,
    changed: 0,
  };
}

function repriceRow(account) {
  const source = account || {};
  const items = source.items || [];
  const totals = source.totals || {};
  const before = repriceSide(totals.from);
  const after = repriceSide(totals.to);
  return {
    ...blankRow(source),
    number: source.referral || null,
    code: source.code || null,
    items: items.length,
    changed: changedCount(items),
    before,
    after,
    diff: repriceDiff(before, after),
    blocked: null,
  };
}

function repriceFailure(failure) {
  const source = failure || {};
  const empty = repriceSide(null);
  return {
    ...blankRow(source),
    before: empty,
    after: empty,
    diff: repriceDiff(empty, empty),
    blocked: { reason: source.reason || null, message: source.message || '' },
  };
}

function normalizeRepriceMany(data) {
  const source = data || {};
  const pending = (source.accounts || []).map(repriceRow);
  const blocked = (source.failures || []).map(repriceFailure);
  const before = sumSides(pending, 'before');
  const after = sumSides(pending, 'after');
  return {
    accounts: [...pending, ...blocked],
    changed: pending.filter(account => account.changed > 0).length,
    blocked: blocked.length,
    before,
    after,
    diff: repriceDiff(before, after),
  };
}

export function repricePreview(id) {
  return http.get(`${BASE}/${id}/reprice`).then(({ data }) => normalizeReprice(data));
}

export function repriceApply(id) {
  return http.post(`${BASE}/${id}/reprice`, {}).then(({ data }) => withSnapshot(data));
}

export function repricePreviewMany(ids) {
  return http.post(`${BASE}/reprice`, { ids, dryRun: true })
    .then(({ data }) => normalizeRepriceMany(data));
}

export function repriceApplyMany(ids) {
  return http.post(`${BASE}/reprice`, { ids }).then(({ data }) => {
    const source = data || {};
    const applied = (source.accounts || []).map(account => ({
      id: idOf(account),
      ok: true,
      changed: changedCount(account.items),
      reason: null,
      message: '',
    }));
    const failed = (source.failures || []).map(failure => ({
      id: idOf(failure),
      ok: false,
      changed: 0,
      reason: failure.reason || null,
      message: failure.message || '',
    }));
    return {
      results: [...applied, ...failed],
      updated: applied.length,
      failed: failed.length,
    };
  });
}

export function loadPlan(insuranceId, planId) {
  return http.get(`/insurances/${insuranceId}/plans/${planId}`).then(({ data }) => data);
}

export function loadAppointment(id) {
  return http.get(`/appointments/${id}`).then(({ data }) => data);
}

export function printRoute(id) {
  return `${BASE}/${id}/print`;
}

export function printRequestRoute(id) {
  return `${BASE}/${id}/print-request`;
}

export function batchLimitOf(planSettings) {
  const billing = planSettings && planSettings.general ? planSettings.general.billing : null;
  const accounts = billing && billing.accounts ? billing.accounts : {};
  if (accounts.enabled === false) return 100;
  const quantity = Number(accounts.quantity) || 0;
  return quantity > 0 ? quantity : 100;
}

export function reducersOf(planSettings) {
  const billing = planSettings && planSettings.general ? planSettings.general.billing : null;
  const reducers = billing && Array.isArray(billing.reducers) && billing.reducers.length > 0
    ? billing.reducers
    : [100, 70, 50, 50];
  return [...new Set(reducers.map(Number).filter(value => value > 0))]
    .sort((a, b) => b - a)
    .map(percent => ({ value: percent / 100, label: `${percent}%` }));
}

export function toDate(value) {
  if (!value) return '';
  const date = moment(value);
  return date.isValid() ? date.format('YYYY-MM-DD') : '';
}

export function toTime(value) {
  if (!value) return '';
  const date = moment(value);
  return date.isValid() ? date.format('HH:mm') : '';
}

export function errorReason(e) {
  const data = e && e.response && e.response.data ? e.response.data : {};
  const details = data.data || data.details || {};
  return details.reason || data.reason || null;
}
